import type {
  DesktopPlatform,
  DownloadChannel,
  DownloadManifest,
  ExtensionManifest
} from '../../shared-types/src/index.js';
import { SERVICE_ROUTES, type ServiceClient } from './index.js';

interface HttpDownloadsDeps {
  call: (
    routeName: 'downloadChannels',
    body: unknown,
    accessToken: string
  ) => Promise<unknown>;
  rawCall: (
    method: string,
    path: string,
    body: unknown,
    accessToken: string | undefined
  ) => Promise<unknown>;
  interpolate: (
    path: string,
    params: Record<string, string | undefined>
  ) => string;
  withQuery: (path: string, query: object) => string;
}

export type HttpDownloads = Pick<
  ServiceClient,
  'downloadChannels' | 'downloadLatest' | 'extensionLatest'
>;

export function createHttpDownloads(deps: HttpDownloadsDeps): HttpDownloads {
  return {
    async downloadChannels(accessToken: string): Promise<DownloadChannel[]> {
      return (await deps.call(
        'downloadChannels',
        undefined,
        accessToken
      )) as DownloadChannel[];
    },

    async downloadLatest(
      accessToken: string,
      channel: DownloadChannel,
      platform: DesktopPlatform
    ): Promise<DownloadManifest> {
      const path = deps.interpolate(SERVICE_ROUTES.downloadLatest.path, {
        channel,
        platform
      });
      const result = await deps.rawCall('GET', path, undefined, accessToken);
      return result as DownloadManifest;
    },

    async extensionLatest(
      accessToken: string,
      extensionId: string,
      platform: 'win32' | 'darwin',
      request: { appVersion: string; protocolVersion: number }
    ): Promise<ExtensionManifest> {
      const path = deps.interpolate(SERVICE_ROUTES.extensionLatest.path, {
        extensionId,
        platform
      });
      return (await deps.rawCall(
        'GET',
        deps.withQuery(path, request),
        undefined,
        accessToken
      )) as ExtensionManifest;
    }
  };
}
